'use client';

import { useEffect, useState } from 'react';
import { createClient } from '@/lib/supabase/client';

interface Shipment {
  id: string;
  trackingNumber: string;
  status: string;
  origin: string;
  destination: string;
  currentLocation: string | null;
  weight: string | null;
  dimensions: string | null;
  estimatedDelivery: string | null;
  orderDate: string;
  createdAt: string;
  updatedAt: string;
}

interface TrackingEvent {
  id: string;
  shipmentId: string;
  eventType: string;
  status: string;
  location: string;
  countryCode: string | null;
  description: string | null;
  completed: boolean;
  timestamp: string;
}

interface DestinationStop {
  id: string;
  shipmentId: string;
  location: string;
  city: string;
  country: string;
  countryCode: string | null;
  stopType: string;
  stopOrder: number;
  latitude: number | null;
  longitude: number | null;
  completed: boolean;
  completedAt: string | null;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;

  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }

  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];

    return schemaErrorPatterns.some((pattern) => pattern.test(error.message));
  }

  return false;
}

function convertShipmentFromDb(row: any): Shipment {
  return {
    id: row.id,
    trackingNumber: row.tracking_number,
    status: row.status,
    origin: row.origin,
    destination: row.destination,
    currentLocation: row.current_location,
    weight: row.weight,
    dimensions: row.dimensions,
    estimatedDelivery: row.estimated_delivery,
    orderDate: row.order_date,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function convertEventFromDb(row: any): TrackingEvent {
  return {
    id: row.id,
    shipmentId: row.shipment_id,
    eventType: row.event_type,
    status: row.status,
    location: row.location,
    countryCode: row.country_code,
    description: row.description,
    completed: row.completed,
    timestamp: row.timestamp,
  };
}

function convertStopFromDb(row: any): DestinationStop {
  return {
    id: row.id,
    shipmentId: row.shipment_id,
    location: row.location,
    city: row.city,
    country: row.country,
    countryCode: row.country_code,
    stopType: row.stop_type,
    stopOrder: row.stop_order,
    latitude: row.latitude,
    longitude: row.longitude,
    completed: row.completed,
    completedAt: row.completed_at,
  };
}

export function useRealtimeShipments(userId: string | null) {
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const supabase = createClient();

  useEffect(() => {
    if (!userId) {
      setShipments([]);
      setLoading(false);
      return;
    }

    // Initial fetch
    const fetchShipments = async () => {
      try {
        const { data, error } = await supabase
          .from('shipments')
          .select('*')
          .eq('user_id', userId)
          .order('created_at', { ascending: false });

        if (error) {
          if (isSchemaError(error)) {
            console.error('Schema error:', error.message);
            throw error;
          } else {
            console.log('Data error:', error.message);
            setError(error.message);
            setShipments([]);
          }
        } else {
          setShipments(data?.map(convertShipmentFromDb) || []);
        }
      } catch (err: any) {
        console.log('Schema-related error:', err.message);
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    };

    fetchShipments();

    // Real-time subscription
    const channel = supabase
      .channel('shipments_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'shipments',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setShipments((current) => [convertShipmentFromDb(payload.new), ...current]);
          } else if (payload.eventType === 'UPDATE') {
            setShipments((current) =>
              current?.map((shipment) =>
                shipment?.id === payload?.new?.id ? convertShipmentFromDb(payload.new) : shipment
              ) || []
            );
          } else if (payload.eventType === 'DELETE') {
            setShipments((current) => current?.filter((shipment) => shipment?.id !== payload?.old?.id) || []);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId]);

  return { shipments, loading, error };
}

export function useRealtimeTrackingEvents(shipmentId: string | null) {
  const [events, setEvents] = useState<TrackingEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const supabase = createClient();

  useEffect(() => {
    if (!shipmentId) {
      setEvents([]);
      setLoading(false);
      return;
    }

    // Initial fetch
    const fetchEvents = async () => {
      try {
        const { data, error } = await supabase
          .from('tracking_events')
          .select('*')
          .eq('shipment_id', shipmentId)
          .order('timestamp', { ascending: true });

        if (error) {
          if (isSchemaError(error)) {
            console.error('Schema error:', error.message);
            throw error;
          } else {
            console.log('Data error:', error.message);
            setError(error.message);
            setEvents([]);
          }
        } else {
          setEvents(data?.map(convertEventFromDb) || []);
        }
      } catch (err: any) {
        console.log('Schema-related error:', err.message);
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();

    // Real-time subscription
    const channel = supabase
      .channel('tracking_events_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'tracking_events',
          filter: `shipment_id=eq.${shipmentId}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setEvents((current) => [...current, convertEventFromDb(payload.new)].sort((a, b) => 
              new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            ));
          } else if (payload.eventType === 'UPDATE') {
            setEvents((current) =>
              current?.map((event) => (event?.id === payload?.new?.id ? convertEventFromDb(payload.new) : event)) || []
            );
          } else if (payload.eventType === 'DELETE') {
            setEvents((current) => current?.filter((event) => event?.id !== payload?.old?.id) || []);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [shipmentId]);

  return { events, loading, error };
}

export function useRealtimeDestinationStops(shipmentId: string | null) {
  const [stops, setStops] = useState<DestinationStop[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const supabase = createClient();

  useEffect(() => {
    if (!shipmentId) {
      setStops([]);
      setLoading(false);
      return;
    }

    // Initial fetch
    const fetchStops = async () => {
      try {
        const { data, error } = await supabase
          .from('destination_stops')
          .select('*')
          .eq('shipment_id', shipmentId)
          .order('stop_order', { ascending: true });

        if (error) {
          if (isSchemaError(error)) {
            console.error('Schema error:', error.message);
            throw error;
          } else {
            console.log('Data error:', error.message);
            setError(error.message);
            setStops([]);
          }
        } else {
          setStops(data?.map(convertStopFromDb) || []);
        }
      } catch (err: any) {
        console.log('Schema-related error:', err.message);
        setError(err.message);
        throw err;
      } finally {
        setLoading(false);
      }
    };

    fetchStops();

    // Real-time subscription
    const channel = supabase
      .channel('destination_stops_changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'destination_stops',
          filter: `shipment_id=eq.${shipmentId}`,
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setStops((current) => [...current, convertStopFromDb(payload.new)].sort((a, b) => a.stopOrder - b.stopOrder));
          } else if (payload.eventType === 'UPDATE') {
            setStops((current) =>
              current?.map((stop) => (stop?.id === payload?.new?.id ? convertStopFromDb(payload.new) : stop)) || []
            );
          } else if (payload.eventType === 'DELETE') {
            setStops((current) => current?.filter((stop) => stop?.id !== payload?.old?.id) || []);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [shipmentId]);

  return { stops, loading, error };
}